import AbstractHTML5Player from '../all/AbstractHTML5Player';
import Utils from '../../utils/Utils';
import {
  PONCONNECT,
  PONSTOP,
  PONPLAY,
  PONPAUSE,
  PONSKIP,
  PONSPEED,
  PONBUFFER
} from '../all/settings';

import ManifestLanguagesTask from '../../requests/tasks/video/ManifestLanguagesTask';
import {
  SS,
  SS_MA,
  AUDIO,
  PLAYERIMAGE,
  HLS,
  HLSPRM,
  RADIO,
  HLS_KR,
  onRenderError
} from '../../utils/playerConstants';
import RequestManager from '../../requests/RequestManager';
import PlayerUtil from './PlayerUtil';
import * as constant from './../../AAFPlayer/constants';
import logger from 'src/utils/logger/logger';

const APP_ID = 'com.clarovideo';
const DRM_TYPE = 'playready';
const DRM_SYSTEM_ID = 'urn:dvb:casystemid:19219';

class Web0sPlayer extends AbstractHTML5Player {
  constructor() {
    super();
    this.options = {};
    this._playerContainer = null;
    this._playerHTMLTag = null;
    this._isPrepared = false;
    this._duration = 0;
    this._isOnLoaded = false;
    this.seek_resume = 0;
    this.src = null;

    this.prependPip = '';

    this.audioTracksReady = false;
    this.web0sClientId = null;
    this.web0sReady = false;
    this.web0sInitiatorMsg = null;
    this.web0sMsgId = null;

    this.retryLimitPlay = 30;
    this.retryIntervalPlay = 1;

    logger.debug('[Web0sPlayer] constructor');

    this.retryIntervalFirstPlaying = 0.9;
    this.retryLimitFirstPlaying = 40;
    this.seekWindow = null;

    this.webOS = window.webOS;

    // binds
    this.unloadWebosDrmClient = this.unloadWebosDrmClient.bind(this);
    this.loadWebosDrmClient = this.loadWebosDrmClient.bind(this);
    this.setInitiatorInformation = this.setInitiatorInformation.bind(this);
    this.loadDrmClient = this.loadDrmClient.bind(this);
    this.sendDRMInformation = this.sendDRMInformation.bind(this);
    this.subscribeLicensingError = this.subscribeLicensingError.bind(this);
    this.getWebosMediaId = this.getWebosMediaId.bind(this);
    this.onSuccessChangeAudio = this.onSuccessChangeAudio.bind(this);
    this.seekTimeshift = this.seekTimeshift.bind(this);
    this.tryOnFirstTimeshiftSeek = this.tryOnFirstTimeshiftSeek.bind(this);

    this.disableHTML5AudioTracks = this.disableHTML5AudioTracks.bind(this);
    this.nextDisableHTML5AudioTracks = this.nextDisableHTML5AudioTracks.bind(this);
    this.sdkVersion = null;

    // Parse manifest on device or middleware to get languages and DRM
    this.parseManifestOnDevice = true;

    this.playerUtil = new PlayerUtil();
    this.playerStarts = false;
  }

  createPlayer(options) {
    this.prependPip = options.isPip ? '_pip' : '_full';
    this._playerContainer = window.document.createElement('div');
    this._playerContainer.style.backgroundColor = 'transparent';
    this._playerContainer.id = `Html5PlayerContainer${this.prependPip}`;
    this._playerContainer.className = 'Html5PlayerContainer';

    if (!this.streamIsImage()) {
      this.setPlayerBackground(options.backgroundImage ? options.backgroundImage : null);

      this._playerHTMLTag = window.document.createElement(
        this.streamIsAudio() ? 'audio' : 'video'
      );
      this._playerHTMLTag.setAttribute('id', `Html5Player${this.prependPip}`);

      // Muted only apply to pip, at this moment
      if (options.muted) {
        this._playerHTMLTag.setAttribute('muted', 'muted');
      }

      this._playerHTMLTag.className = 'Html5Player';
      this._playerContainer.appendChild(this._playerHTMLTag);
    } else {
      this.setPlayerBackground(options.src ? options.src : null);
    }

    if (!this.options.parentWrapper) {
      logger.error('parentWrapper not found');
      return;
    }

    const vWrapper = document.getElementById(this.options.parentWrapper.id);
    // vWrapper.appendChild(this._playerContainer);
    vWrapper.insertBefore(this._playerContainer, vWrapper.firstChild);

    // Add listeners...if apply
    if (!this.streamIsImage()) {
      this.addEventsListeners();
    }
  }

  loadWebOSAPI() {
    if (!this.webOS) {
      this.webOS = window.webOS;
    }
  }

  createMedia(options) {

    logger.debug('[WEB0S PLAYER] Web0s player createMedia');
    logger.debug(options);

    // Load webOS if not
    this.loadWebOSAPI();

    if (this._isPrepared) return;

    this.audioTracksReady = false;
    this.web0sClientId = null;
    this.web0sInitiatorMsg = null;
    this.web0sMsgId = null;
    this.web0sReady = false;

    // Just in case...newSource attr only arrive here when it is a replaceMediaSource (enter in replace method below),
    // so in this path we dont have to have this attr, reset it
    if (options.newSource && options.newSource.src) {
      options.newSource = null;
    }

    this.options = options;
    if (this.options.isPip) {
      // Pip would only exist when this is a video or audio
      this._playerHTMLTag = document.getElementById('Html5Player_pip');
      if (!this._playerHTMLTag) {
        this.createPlayer(options);
      } else {
        this.addEventsListeners();
        this._playerContainer = document.getElementById('Html5PlayerContainer_pip');
        if (!this.streamIsImage()) {
          this.setPlayerBackground(
            options.backgroundImage ? options.backgroundImage : null
          );
        }
        if (options.muted) {
          this._playerHTMLTag.setAttribute('muted', 'muted');
        } else {
          this._playerHTMLTag.removeAttribute('muted');
        }
        if (options.controls) {
          this._playerHTMLTag.setAttribute('controls', 'controls');
        } else {
          this._playerHTMLTag.removeAttribute('controls');
        }
      }
    } else {
      this.createPlayer(options);
    }

    this.hide();
    this._isPrepared = true;
  }

  async setLanguagesFromManifest(manifest) {
    let langsResult = [];
    if (manifest) {
      if (this.parseManifestOnDevice) {
        this.audioTracks = await this.playerUtil.getLanguagesFromManifestDevice(manifest);
      } else {
        const manifestLanguagesTask = new ManifestLanguagesTask(manifest);
        langsResult = await RequestManager.addRequest(manifestLanguagesTask);

        logger.debug('[WEB0S PLAYER] setLanguagesFromManifest, raw result> ', langsResult);

        if (langsResult && langsResult.languages) {
          this.audioTracks = langsResult.languages;
        }
      }
    }

    this.audioTracksReady = true;
    logger.debug('[WEB0S PLAYER] setLanguagesFromManifest, langs> ', this.audioTracks);

    return this.audioTracks;
  }

  async getDrmUrl(manifest) {
    const DRMUrl = this.parseManifestOnDevice
      ? await this.playerUtil.getDRMFromDevice(manifest)
      : await this.playerUtil.getDRMFromMiddleware(manifest);
    logger.debug('[WEB0S PLAYER] getDrmUrl, raw result> ', DRMUrl);

    return DRMUrl;
  }

  getXMLInitiator(drmUrl, customData) {
    let xml_replace = '<?xml version="1.0" encoding="utf-8"?>';
    xml_replace +=
      '<PlayReadyInitiator xmlns="http://schemas.microsoft.com/DRM/2007/03/protocols/">';
    xml_replace += '<LicenseServerUriOverride>';
    xml_replace += '<LA_URL>{{drm_url}}</LA_URL>';
    xml_replace += '</LicenseServerUriOverride>';
    xml_replace += '<SetCustomData>';
    xml_replace += '<CustomData>{{customData}}</CustomData>';
    xml_replace += '</SetCustomData>';
    xml_replace += '</PlayReadyInitiator>';

    xml_replace = xml_replace.replace('{{drm_url}}', drmUrl);
    xml_replace = xml_replace.replace('{{customData}}', customData);

    return xml_replace;
  }

  async loadMedia() {
    logger.debug('[WEB0S PLAYER] INIT AbstractHTML5Player loadMedia');
    if (!this._isPrepared) {
      return false;
    }
    logger.debug('[WEB0S PLAYER] INIT AbstractHTML5Player loadMedia 2');
    if (this.streamIsImage()) {
      return;
    }

    if (this.streamIsVideo() && this.options.resume && !this.options.isLive) {
      this.seek_resume = this.options.resume;
    }

    // Default videoType
    let mediaType = 'video/mp4';
    if (this.options.src) {
      this.src = this.options.src;
      this._isOnLoaded = false;

      if (this.options.streamType === SS || this.options.streamType === SS_MA) {
        mediaType = 'application/vnd.ms-playready.initiator+xml';
        // application/vnd.ms-sstr+xml

        // check if we have drm server
        // Se delimita a proveedor HBO para no obtener server_url desde manifest y dejar la misma lógica en demas proveedores.
        if (
          !this.options.drmInfo.server_url &&
          this.options.provider.toUpperCase() !== 'HBO'
        ) {
          this.options.drmInfo.server_url = await this.getDrmUrl(this.options.src);
        }

        const { server_url } = this.options.drmInfo;
        const challenge = (this.options.drmInfo && this.options.drmInfo.challenge) || '';
        const content_id = (this.options.drmInfo && this.options.drmInfo.content_id) || '';
        const device_id = this.options.drmInfo.device_id || '';

        let customData = '';
        if (challenge && challenge.indexOf('tokenID') !== -1) {
          customData = challenge;
        } else if (challenge) {
          customData = btoa(`{"customdata":${challenge},"device_id": "${device_id}"}`);
        }

        this.mediaType = mediaType;

        if (this.options.drmInfo.server_url) {
          this.web0sInitiatorMsg = this.getXMLInitiator(
            this.options.drmInfo.server_url,
            customData
          );

          logger.debug('[WEB0S PLAYER] XML to send> ', this.web0sInitiatorMsg);

          this.loadWebosDrmClient();
        } else {
          this.web0sReady = true;
          // for SS => application/vnd.ms-sstr+xml
          this.setVideoSource(this.options.src, 'application/vnd.ms-sstr+xml');
        }
      }
      if (this.options.streamType === HLS || this.options.streamType === HLS_KR) {
        this.web0sReady = true;
        mediaType = 'application/vnd.apple.mpegurl';
        this.mediaType = mediaType;
        this.setVideoSource(this.options.src, mediaType);
      }
      if (this.options.streamType === AUDIO || this.options.streamType === RADIO) {
        this.web0sReady = true;
        mediaType = 'audio/mp4';
        this.mediaType = mediaType;
        this.setVideoSource(this.options.src, mediaType);
      }

      if (!this.options.isPip) {
        this.setPlayerFull();
      }
    } else {
      this.destroy();
    }
    logger.debug('[WEB0S PLAYER] END WEB0S player loadMedia');
  }

  setPlayerFull() {
    this.setPlayerSize(0, 0, 1280, 720);
  }

  setPlayerSize(top, left, width, height) {
    if (this.options.parentWrapper) {
      const vWrapper = document.getElementById(this.options.parentWrapper.id);
      if (vWrapper && vWrapper.parentNode) {
        vWrapper.parentNode.style.top = `${top}px`;
        vWrapper.parentNode.style.left = `${left}px`;
        vWrapper.parentNode.style.width = `${width}px`;
        vWrapper.parentNode.style.height = `${height}px`;
      }
    }

    if (this._playerContainer) {
      this._playerContainer.style.top = '0px';
      this._playerContainer.style.left = '0px';
      this._playerContainer.style.width = `${width}px`;
      this._playerContainer.style.height = `${height}px`;
      this._playerContainer.style.position = 'relative';
      this._playerContainer.className = 'AbstractPlayerObject';
    }

    if (this.streamIsAudio()) {
      width = 0;
      height = 0;
      if (this._playerHTMLTag) {
        this._playerHTMLTag.style.top = '0px';
        this._playerHTMLTag.style.left = '0px';
        this._playerHTMLTag.style.width = `${width}px`;
        this._playerHTMLTag.style.height = `${height}px`;
        this._playerHTMLTag.style.position = 'relative';
        this._playerHTMLTag.className = 'web0s-html5-player';
      }
    }
  }

  setVideoSource(url, type) {
    logger.debug('[WEB0S PLAYER] INIT WEB0S Player setVideoSource');
    const source = document.createElement('source');
    const options = {};
    options.option = {};
    let mediaOption = '';

    //Se integra setteo de bitrates de inicio en reproducción
    options.option.adaptiveStreaming = {};
    options.option.adaptiveStreaming.bps = {};
    options.option.adaptiveStreaming.bps.start = 4000000;

    if (this.options.streamType === SS || this.options.streamType === SS_MA) {

      if (this.options.drmInfo.server_url) {
        options.option.drm = {};
        options.option.drm.type = DRM_TYPE;
        options.option.drm.clientId = this.web0sClientId;
      }

      if (this.seek_resume > 0) {
        logger.debug('[WEB0S PLAYER] hay starttime> ', this.seek_resume);
        options.option.transmission = {};
        options.option.transmission.playTime = {};
        options.option.transmission.playTime.start = this.seek_resume * 1000;
      } else {
        logger.debug('[WEB0S PLAYER] NO hay starttime> setting default 500');
        options.option.transmission = {};
        options.option.transmission.playTime = {};
        options.option.transmission.playTime.start = 500;
      }

      mediaOption = escape(JSON.stringify(options));
      // source.setAttribute('type', 'application/vnd.ms-sstr+xml;mediaOption=' + mediaOption);
      source.setAttribute('type', `${type};mediaOption=${mediaOption}`);

      logger.debug(`[WEB0S] HTML5 player __addSourceToVideoPlayready: ${mediaOption}`);
      logger.debug(`[WEB0S] HTML5 player __addSourceToVideoPlayready: ${this.WEBOSClientId}`);
      logger.debug(`[WEB0S] HTML5 player __addSourceToVideoPlayready: ${this.WEBOSReady}`);
      logger.debug(`[WEB0S] HTML5 player __addSourceToVideoPlayready: ${type}`);
      logger.debug(`[WEB0S] HTML5 player startTime play: ${this._playTimeStart}`);

      this.seek_resume = 0;
    } else {
      mediaOption = escape(JSON.stringify(options));
      // source.setAttribute('type', 'application/vnd.ms-sstr+xml;mediaOption=' + mediaOption);
      source.setAttribute('type', `${type};mediaOption=${mediaOption}`);
      //source.setAttribute('type', type);
    }

    source.setAttribute('src', this.options.src);
    source.id = `Html5PlayerSource${this.options.isPip ? '_pip' : '_full'}`;

    this._playerHTMLTag.innerText = '';
    this._playerHTMLTag.appendChild(source);
    this._playerHTMLTag.load();

    logger.debug('******* <<<<<<<<<<< END WEB0S PLAYER setVideoSource');
  }

  async tryPlay(currentTry) {
    if (this.web0sReady === false) {
      logger.debug(`[WEB0S PLAYER]> trying play, retry number: [${currentTry}]`);
      if (currentTry >= this.retryLimitPlay) {
        logger.error('[WEB0S PLAYER]> Error, player instance could not initialize');
        return;
      }
      await Utils.sleep(this.retryIntervalPlay);

      return this.tryPlay(++currentTry);
    }
    logger.debug('[WEB0S PLAYER]> player is READY to play, returns from sendDRM');
    return this.play();
  }

  play() {
    if (this.streamIsImage()) {
      return;
    }

    if (this.web0sReady === false) {
      this.tryPlay(1);
    }

    logger.debug('[WEB0S PLAYER] INIT play');
    // this.show();

    this._playerHTMLTag.play();
    logger.debug('[WEB0S PLAYER] END play');
  }

  resume() {
    logger.debug('[WEB0S PLAYER] END resume');
    this._playerHTMLTag.play();
  }

  pause() {
    this._playerHTMLTag.pause();
    this.onPlayerStatePause();
  }

  stop() {
    this._playerHTMLTag.pause();
    this.onPlayerStateStop();
  }

  async replaceMediaSource(newSource) {
    logger.debug('[WEB0S PLAYER] enter replaceMediaSource> ', newSource);
    this.options.newSource = newSource; // Add the attr to detect error when replace and not reset whole player
    this.options.src = newSource.src;
    this.options.drmInfo = newSource.drmInfo;
    this.options.isLive = newSource.isLive;
    this.options.streamType = newSource.streamType;

    if (newSource.backgroundImage && !this.streamIsAudio()) {
      this.options.backgroundImage = newSource.backgroundImage;
      this.setPlayerBackground(newSource.backgroundImage);
    }

    // If there is a resume when vod...
    if (newSource.resume && !newSource.isLive) {
      this.seek_resume = newSource.resume;
      this.options.resume = newSource.resume;
    }

    // Stop already take care of send stop to player internal state
    // so below, play change to playingstate
    this.pause();
    this.playerStarts = false;

    const currentSource = document.getElementById(
      `Html5PlayerSource${this.options.isPip ? '_pip' : '_full'}`
    );
    if (currentSource) {
      currentSource.parentNode.removeChild(currentSource);
    }

    this.audioTracksReady = false;
    this.seekWindow = null;

    let mediaType = '';
    if (this.options.streamType === SS || this.options.streamType === SS_MA) {
      logger.debug('[WEB0S PLAYER] enter replaceMediaSource es SS ');
      mediaType = 'application/vnd.ms-playready.initiator+xml';

      // check if we have drm server
      // Se delimita a proveedor HBO para no obtener server_url desde manifest y dejar la misma lógica en demas proveedores.
      if (!this.options.drmInfo.server_url && this.options.provider.toUpperCase() !== 'HBO') {
        this.options.drmInfo.server_url = await this.getDrmUrl(this.options.src);
      }

      const { server_url } = this.options.drmInfo;
      const challenge = (this.options.drmInfo && this.options.drmInfo.challenge) || '';
      const content_id = (this.options.drmInfo && this.options.drmInfo.content_id) || '';
      const device_id = this.options.drmInfo.device_id || '';

      let customData = '';
      if (challenge && challenge.indexOf('tokenID') !== -1) {
        customData = challenge;
      } else if (challenge) {
        customData = btoa(`{"customdata":${challenge},"device_id": "${device_id}"}`);
      }

      this.mediaType = mediaType;

      if (this.options.drmInfo.server_url) {
        this.web0sInitiatorMsg = this.getXMLInitiator(
          this.options.drmInfo.server_url,
          customData
        );
        if (this.web0sClientId) {
          // keep temp webOs, and await playing until get new drm response
          this.web0sReady = false;
          this.sendDRMInformation()
            .then(() => {
              // for SS  => application/vnd.ms-sstr+xml
              this.setVideoSource(this.options.src, 'application/vnd.ms-sstr+xml');
              this.web0sReady = true;
            })
            .catch(msg => {
              this._onError(onRenderError, msg);
            });
        } else {
          this.web0sReady = false;
          this.loadWebosDrmClient();
        }
      } else {
        this.web0sReady = true;
        this.setVideoSource(this.options.src, this.mediaType);
      }
    }
    if (this.options.streamType === HLS || this.options.streamType === HLS_KR) {
      logger.debug('[WEB0S PLAYER] enter replaceMediaSource es HLS ');
      this.web0sReady = true;
      mediaType = 'application/vnd.apple.mpegurl';
      this.mediaType = mediaType;
      this.setVideoSource(this.options.src, mediaType);
    }
    if (this.options.streamType === AUDIO || this.options.streamType === RADIO) {
      logger.debug('[WEB0S PLAYER] enter replaceMediaSource es AUDIO ');
      this.web0sReady = true;
      mediaType = 'audio/mp4';
      this.mediaType = mediaType;
      this.setVideoSource(this.options.src, mediaType);
    }
    this.hide();
    logger.debug('[WEB0S PLAYER] enter replaceMediaSource going to play method ');
    // Play ahora se controla desde onresolve AAFPlayer
    // this.play();
  }

  /* PLAYER CONTROL */
  /**
   *  @param timeshiftData = {
   *    backward: isBackward,
   *    // Si es negativo o no, se verifica en video/component, quien es quien decide
   *    // si se va o no a pgm o solo hace seek sobre el actual playing
   *    seektime: this.state.channelInfo.timeshiftAllowed - this.state.timeshiftTime ,
   *    maxtimeshiftallowed,
   *    starttime: tiempo inicial en que se pidió el request a getmedia
   *  };
   *
   * @param firstSeek true => se tiene que esperar hasta que el player inicie el playing y luego de esto, se manda el seek.
   *                  false => el player ya esta playeando, sobre el playing se manda el seek (no se espera por el playing).
   */
  // Based on currentTime
  seekTimeshift(timeshiftData, firstSeek) {
    logger.info('[WEB0S PLAYER] seekTimeshift ', timeshiftData, firstSeek);
    if (firstSeek) {
      this.tryOnFirstTimeshiftSeek(1, timeshiftData);
    } else {
      let secondTo = Math.floor(timeshiftData.maxtimeshiftallowed - timeshiftData.seektime);
      if (secondTo < this.seekWindow.timeshiftstart) {
        secondTo = this.seekWindow.timeshiftstart;
      }
      if (secondTo >= this.seekWindow.maxtimeshiftallowed) {
        secondTo = this.seekWindow.maxtimeshiftallowed - 5; // To protect player, does it work?
      }
      logger.debug(
        '[WEB0S PLAYER] trying to do timeshift seek to: <secondTo/seekWindow>  ',
        secondTo,
        this.seekWindow
      );
      this.seek(secondTo);
    }
  }

  async tryOnFirstTimeshiftSeek(currentTry, timeshiftData) {
    logger.debug(
      `[WEB0S PLAYER]> tryOnFirstTimeshiftSeek getCurrentTime: [${this.getCurrentTime()}]`
    );
    logger.info('[WEB0S PLAYER] tryOnFirstTimeshiftSeek duration ', this.getDuration());
    if (this.getCurrentTime() === 0) {
      logger.debug(`[WEB0S PLAYER]> tryOnFirstTimeshiftSeek retry number: [${currentTry}]`);
      if (currentTry >= this.retryLimitFirstPlaying) {
        throw new Error('Fail to get playing state');
        logger.error('[WEB0S PLAYER]> Error, Fail to get playing state');
        return;
      }
      await Utils.sleep(this.retryIntervalFirstPlaying);

      return this.tryOnFirstTimeshiftSeek(++currentTry, timeshiftData);
    }
    logger.debug('[TIZEN PLAYER]> player is ready and playing');
    this.seekWindow = {
      timeshiftstart: Math.ceil(this.getCurrentTime())
    };
    return this.seekTimeshift(timeshiftData, false);
  }

  // sec in seconds
  seek(seconds) {
    logger.debug(`[WEB0S PLAYER] seek de: ${seconds}`);
    return this.skip(seconds);
  }

  skip(seconds) {
    this._playerHTMLTag.currentTime = seconds;
  }

  hide() {
    if (this._playerHTMLTag) {
      this._playerHTMLTag.style.visibility = 'hidden';
    }

    // if (this._playerContainer) {
    // this._playerContainer.style.visibility = "hidden";
    // }
  }

  show() {
    logger.debug('[WEB0S PLAYER] enter show');
    if (this._playerHTMLTag) {
      this._playerHTMLTag.style.visibility = 'visible';
    }
  }

  destroy() {
    if (!this._isPrepared) {
      return;
    }

    /*
// if we are un epg and did a replace, we dont need to destroy player
// we come from a replace
if (this.options.newSource && this.options.newSource.src) {
  this.audioTracksReady = false;
  return;
}
*/

    // Both, pip and full, audio and video, stop and remove listeners
    if (!this.streamIsImage()) {
      this.stop();
      this.removeEventsListener();
    }

    this.unloadWebosDrmClient();

    this.audioTracksReady = false;
    this.web0sClientId = null;
    this.web0sInitiatorMsg = null;
    this.web0sMsgId = null;
    this.web0sReady = false;
    this.seekWindow = null;

    if (this.options.isPip) {
      this.destroyPip();
      if (this.streamIsImage()) {
        this.destroyPlayerContainer();
      }
    } else {
      this.destroyPlayerContainer();
    }

    this.playerStarts = false;
    // Remove background both pip and full
    this.setPlayerBackground(null);

    this.previousPlayerState = PONCONNECT;
    this.currentPlayerState = PONCONNECT;

    this._isPrepared = false;
    this.seek_resume = 0;
  }

  // For optimize pip playing, we dont destroy pip, we only change source
  destroyPip() {
    if (!this._isPrepared) {
      return;
    }

    // Only remove source and hide player when audio or video
    if (!this.streamIsImage()) {
      const source_dom = document.getElementById('Html5PlayerSource_pip');
      if (source_dom) {
        source_dom.parentNode.removeChild(source_dom);
      }
      this.hide();
    }
  }

  destroyPlayerContainer() {
    if (this._playerContainer) {
      if (this._playerHTMLTag && this._playerHTMLTag.parentNode) {
        this._playerHTMLTag.parentNode.removeChild(this._playerHTMLTag);
      }
      const vWrapper = document.getElementById(this.options.parentWrapper.id);
      vWrapper.removeChild(this._playerContainer);
      this._playerContainer = null;
      this._playerHTMLTag = null;
    }

    this.audioTracks = [];
    this.currentAudioTrackIndex = null;
  }

  unloadWebosDrmClient() {
    logger.debug('[WEB0S PLAYER] unloadWebosDrmClient init');
    if (this.web0sClientId) {
      logger.debug('[WEB0S PLAYER] unloadWebosDrmClient init 1');
      const requestt = this.webOS.service.request('luna://com.webos.service.drm', {
        method: 'unload',
        parameters: { clientId: this.web0sClientId },
        onSuccess: result => {
          logger.debug('[WEB0S PLAYER] DRM Client is unloaded successfully.');
        },
        onFailure: result => {
          logger.error('[WEB0S PLAYER] Fail to unload LG DRM client');
        }
      });
    }
  }

  loadWebosDrmClient() {
    logger.debug('[WEB0S PLAYER] iniciando loadWebosDrmClient...');

    this.setInitiatorInformation()
      .then(resp => {
        logger.debug('[WEB0S PLAYER] success loadWebosDrmClient...', resp[0]);

        this.web0sClientId = resp[0];

        this.sendDRMInformation()
          .then(() => {
            this.web0sReady = true;
            this.setVideoSource(this.options.src, 'application/vnd.ms-sstr+xml');
          })
          .catch(msg => {
            logger.error(
              '[WEB0S PLAYER] error en las promesas en sendDRMInformation',
              msg
            );
            this._onError(onRenderError, 'Error when sending DRM information');
          });
      })
      .catch(msg => {
        logger.error('[WEB0S PLAYER] error en las promesas en loadWebosDrmClient', msg);
        this._onError(onRenderError, 'Error when set DRM initiator information');
      });
  }

  setInitiatorInformation() {
    return new Promise((resolve, reject) => {
      this.loadDrmClient()
        .then(clientId => {
          logger.debug(
            '[WEB0S PLAYER] resueltas todas las promesas en setInitiatorInformation'
          );
          const resp_alt = [];
          resp_alt.push(clientId);
          resolve(resp_alt);
        })
        .catch(msg => {
          logger.error('[WEB0S PLAYER] error en las promesas en setInitiatorInformation');
          reject(msg);
        });
    });
  }

  loadDrmClient() {
    logger.debug('[WEB0S PLAYER] init loadDRMClient');
    return new Promise((resolve, reject) => {
      const requestt = this.webOS.service.request('luna://com.webos.service.drm', {
        method: 'load',
        parameters: {
          drmType: DRM_TYPE,
          appId: APP_ID
        },
        onSuccess: result => {
          const { clientId } = result;
          logger.debug('[WEB0S PLAYER] DRM Client is loaded successfully.');
          resolve(clientId);
        },
        onFailure: result => {
          logger.error(
            `[WEB0S PLAYER] loadDrmClient [${result.errorCode}] ${result.errorText}`
          );
          const err = `${result.errorCode}<>${result.errorText}`;
          // Do something for error handling
          reject(err);
        }
      });
    });
  }

  sendDRMInformation() {
    logger.debug('[WEB0S PLAYER] Entrando a sendDRMInformation method');
    return new Promise((resolve, reject) => {
      let msgId = '';

      const requestt = this.webOS.service.request('luna://com.webos.service.drm', {
        method: 'sendDrmMessage',
        parameters: {
          clientId: this.web0sClientId,
          msgType: this.mediaType,
          msg: this.web0sInitiatorMsg,
          drmSystemId: DRM_SYSTEM_ID
        },
        onSuccess: result => {
          this.web0sMsgId = msgId = result.msgId;
          const { resultCode } = result;
          const { resultMsg } = result;
          logger.debug(`[WEB0S PLAYER] Message ID: ${this.web0sMsgId}`);
          logger.debug(`[WEB0S PLAYER]  [${resultCode}] ${resultMsg}`);
          if (resultCode != 0) {
            this.subscribeLicensingError(this.web0sClientId, result.msgId);
            reject(resultMsg);
          } else {
            resolve(result);
          }
        },
        onFailure: result => {
          logger.error('[WEB0S PLAYER] ERROR en sendDRMINFORMATION');
          logger.error(result);
          this.subscribeLicensingError(this.web0sClientId, msgId);
          const resultCode = 'Error';
          reject(resultCode);
        }
      });
    });
  }

  subscribeLicensingError(clientId, msgId) {
    const requestt = this.webOS.service.request('luna://com.webos.service.drm', {
      method: 'getRightsError',
      parameters: {
        clientId: this.web0sClientId,
        subscribe: true
      },
      onSuccess: result => {
        // Subscription Callback
        logger.debug('[WEB0S PLAYER] ingresando a subscribeLicensingError success:');
        logger.debug(result);
      },
      onFailure: result => {
        logger.error('[WEB0S PLAYER] subscribeLicensing Error ERROR:');
        logger.error(result);
        this._onError(onRenderError, JSON.stringify(result));
      }
    });
  }

  addEventsListeners() {
    // Bind first
    this._toBindOnLoad = this._onLoad.bind(this);
    this._toBindOnWaiting = this._onWaiting.bind(this);
    this._toBindOnTimeUpdate = this._onTimeUpdate.bind(this);
    this._toBindOnWaiting = this._onWaiting.bind(this);
    this._toBindOnPlaying = this._onPlaying.bind(this);
    this._toBindOnPlaying = this._onPlaying.bind(this);
    this._toBindOnError = this._onError.bind(this);
    this._toBindOnFinished = this._onFinished.bind(this);
    this._toBindOnCanPlay = this._onCanPlay.bind(this);
    this._toBindOnDurationChange = this._onDurationChange.bind(this);

    // Add listeners
    this._playerHTMLTag.addEventListener('loadedmetadata', this._toBindOnLoad);
    this._playerHTMLTag.addEventListener('waiting', this._toBindOnWaiting);
    this._playerHTMLTag.addEventListener('timeupdate', this._toBindOnTimeUpdate);
    this._playerHTMLTag.addEventListener('seeking', this._toBindOnWaiting);
    this._playerHTMLTag.addEventListener('seeked', this._toBindOnPlaying);
    this._playerHTMLTag.addEventListener('playing', this._toBindOnPlaying);
    this._playerHTMLTag.addEventListener('error', this._toBindOnError);
    this._playerHTMLTag.addEventListener('ended', this._toBindOnFinished);
    this._playerHTMLTag.addEventListener('canplay', this._toBindOnCanPlay);
    this._playerHTMLTag.addEventListener('durationchange', this._toBindOnDurationChange);
  }

  removeEventsListener() {
    // Remove listeners
    this._playerHTMLTag.removeEventListener('loadedmetadata', this._toBindOnLoad);
    this._playerHTMLTag.removeEventListener('waiting', this._toBindOnWaiting);
    this._playerHTMLTag.removeEventListener('timeupdate', this._toBindOnTimeUpdate);
    this._playerHTMLTag.removeEventListener('seeking', this._toBindOnWaiting);
    this._playerHTMLTag.removeEventListener('seeked', this._toBindOnPlaying);
    this._playerHTMLTag.removeEventListener('playing', this._toBindOnPlaying);
    this._playerHTMLTag.removeEventListener('error', this._toBindOnError);
    this._playerHTMLTag.removeEventListener('ended', this._toBindOnFinished);
    this._playerHTMLTag.removeEventListener('canplay', this._toBindOnCanPlay);
    this._playerHTMLTag.removeEventListener('durationchange', this._toBindOnDurationChange);
  }

  getCurrentTime() {
    let ct = 0;
    if (this._playerHTMLTag && this._playerHTMLTag.currentTime) {
      ct = this._playerHTMLTag.currentTime;
    }

    if(this.options.currentContentType === constant.AAF_CONTENT_IS_TV
      || this.options.currentContentType === constant.AAF_CONTENT_IS_STARTOVER
    ) {
      ct = this._calculateCurrentTimeTS(ct);
      ct = isNaN(ct) ? 0 : ct;
    }

    return ct;
  }

  getDuration() {
    return this._duration;
  }

  setDuration() {
    this._duration = this._playerHTMLTag.duration;
  }

  /* Events */

  _onLoad() {
    logger.debug('[WEB0S PLAYER EVENT] _onLoad');
    // If multiple audio, we get languages
    this.show(); // TODO check here

    if (this.options.events.onLoad) {
      this.options.events.onLoad();
    }
    this._fireNowEvent();
  }

  _onWaiting() {
    logger.debug('[WEB0S PLAYER EVENT] _onWaiting');
    if (this.options.events.onWaiting) {
      this.options.events.onWaiting();
    }
  }

  _onTimeUpdate() {
    logger.debug('[WEB0S PLAYER] onTimeUpdate > ', this.getCurrentTime());
    const currentTime = this.getCurrentTime();

    if (this.options.events.onTimeUpdate) {
      this.options.events.onTimeUpdate(Math.floor(currentTime), currentTime * 1000);
    }
  }

  _onPlaying() {
    logger.debug('[WEB0S PLAYER EVENT] _onPlaying');
    if (this.getCurrentPlayerState() === PONBUFFER) {
      this._onBufferingFinish();
    } else {
      this.onPlayerStatePlay();
    }

    if (this.options.events.onPlaying) {
      this.options.events.onPlaying();
    }
  }

  _onError(e, errorMessage) {
    logger.error('[WEB0S PLAYER EVENT] _onError');
    logger.error(e);
    // From https://dev.w3.org/html5/spec-author-view/video.html
    let msg = '';
    let errorCode = onRenderError;
    if (e.target) {
      switch (e.target.error.code) {
        case e.target.error.MEDIA_ERR_ABORTED:
          msg = 'User aborted the video playback.';
          break;
        case e.target.error.MEDIA_ERR_NETWORK:
          msg = 'A network error caused the video download to fail part-way.';
          break;
        case e.target.error.MEDIA_ERR_DECODE:
          msg =
            'The video playback was aborted due to a corruption problem or because the video used features your browser did not support.';
          break;
        case e.target.error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          msg =
            'The media resource indicated by the src attribute or assigned media provider object was not suitable.';
          break;
        default:
          msg = 'An unknown error occurred.';
          break;
      }
    } else {
      msg = errorMessage;
      errorCode = e;
    }

    this.stop();

    if (this.options.events.onError) {
      this.options.events.onError(msg, errorCode);
    }
  }

  _onFinished() {
    logger.debug('[WEB0S PLAYER EVENT] _onFinished');
    this.onPlayerStateStop();
    // Reinit player
    this.destroy();
    if (this.options.events.onFinished) {
      this.options.events.onFinished();
    }
  }

  _onCanPlay(e) {
    logger.debug('[WEB0S PLAYER EVENT] _onCanPlay> ');
    if (this.options.events.onCanPlay) {
      this.options.events.onCanPlay(e);
    }
    this._playerHTMLTag.play();
    if(!this.audioTracksReady) {
      if (this.options.streamType === SS_MA || this.options.streamType === SS) {
          this.disableHTML5AudioTracks();
          logger.debug('[WEB0S PLAYER EVENT] _onLoad audiotracks');
          this.setLanguagesFromManifest(this.options.src);
      }
      else {
          this.audioTracksReady = true;
      }
    }
  }

  _onDurationChange() {
    logger.debug('[WEB0S PLAYER EVENT] _onDurationChange');
    this._isOnLoaded = true;
    this.setDuration();
    if (this.options.events.onDurationChange) {
      this.options.events.onDurationChange();
    }
  }

  _onBufferingStart() {
    this.onPlayerStateBuffer();

    if (this.options.events.onBufferingStart) {
      this.options.events.onBufferingStart();
    }
  }

  _onBufferingProgress() {
    if (this.options.events.onBufferingProgress) {
      this.options.events.onBufferingProgress();
    }
  }

  _onBufferingFinish() {
    if (this.options.events.onBufferingFinish) {
      this.options.events.onBufferingFinish();
    }

    // If stop and ends buffering, nothing to do
    if (this.getCurrentPlayerState() === PONSTOP) {
      return;
    }

    // If current state is on connecting, then we go to play
    if (this.getCurrentPlayerState() === PONCONNECT) {
      this.onPlayerStatePlay();
    } else {
      // Set same state as last
      this.setCurrentPlayerState(this.previousPlayerState);
    }

    if (this.options.events.onBufferingFinish) {
      this.options.events.onBufferingFinish();
    }
  }

  /* END Events */

  /* MULTIPLE AUDIOTRACKS */
  disableHTML5AudioTracks() {
    if (!this.sdkVersion) {
      const receiveSDKVersion = sdkVersion => {
        this.sdkVersion = sdkVersion;
        this.nextDisableHTML5AudioTracks();
      };
      this.playerUtil.getSDKVersion(receiveSDKVersion);
    } else {
      this.nextDisableHTML5AudioTracks();
    }
  }

  nextDisableHTML5AudioTracks() {
    // Para SDK >= 5 no se desactivan audiotracks
    // TODO verificar por las TVs anteriores, modelos 2019, 2018 ¿?
    if (this.sdkVersion && this.sdkVersion >= 5) {
      return;
    }

    if (this.options.streamType === SS_MA && this.audioTracks && this._playerHTMLTag) {
      try {
        logger.debug(
          '[WEB0S PLAYER] disableHTML5AudioTracks, es multiple, audioTracks> ',
          this._playerHTMLTag.audioTracks
        );
        for (let tt = 0; tt < this._playerHTMLTag.audioTracks.length; tt++) {
          this._playerHTMLTag.audioTracks[tt].enabled = false;
        }
        logger.debug('[WEB0S PLAYER] Terminando disableHTML5AudioTracks');
      } catch (e) {
        logger.debug(
          '[WEB0S PLAYER] disableHTML5AudioTracks catch error (it does not matter, maybe this is LG webos <= 2014)> ',
          e
        );
      }
    }
  }

  async tryGetAudioTracks(currentTry) {
    if (!this.audioTracksReady) {
      logger.debug(`[WEB0S PLAYER]> tryGetAudioTracks retry number: [${currentTry}]`);
      if (currentTry >= this.retryLimitAudioTrack) {
        throw new Error('Fail to get audioTracks');
        logger.error('[WEB0S PLAYER]> Error, player instance could not initialize');
        return;
      }

      // Wait for a while...
      await Utils.sleep(this.retryIntervalAudioTrack);

      // ...Continue recursive...
      return this.tryGetAudioTracks(++currentTry);
    }
    logger.debug(
      `[WEB0S PLAYER]> tryToGetAudioTracks retry number: [${currentTry}] - player audioTracks is READY`
    );

    return this.getAudioTracks();
  }

  getAudioTracks() {
    logger.info('[WEB0S PLAYER]');
    if (!this.audioTracksReady) {
      logger.info('[WEB0S PLAYER] Delay 1s to get the audio track info again');
      // ...Start recursive...
      return this.tryGetAudioTracks(1);
    }

    return this.getAudioTrackById();
  }

  getAudioTrackById() {
    logger.debug('[WEB0S PLAYER] enter getAudioTrackById');
    return new Promise((resolve, reject) => {
      // logger.log('[WEB0S PLAYER] recuperados > ' + JSON.stringify(this.audioTracks), this.audioTracks);
      logger.debug(this.audioTracks.length);
      resolve(this.audioTracks);
    });
  }

  // @codeTrack iso id i.e.: esp, eng, por, ori
  setAudioTrack(codeTrack) {
    logger.debug('[WEB0S PLAYER] enter setAudioTrack> ', codeTrack);

    return new Promise((resolve, reject) => {
      if (!this.audioTracksReady) {
        reject('Dont have audioTracks information');
      }

      const id = parseInt(codeTrack, 10);
      const internalAudioIndex = this.getAudioIndexOfCode(codeTrack);
      if (internalAudioIndex === null) {
        reject('Dont have audioTracks information, index lang does not exist');
      } else if (this.currentAudioTrackIndex === internalAudioIndex) {
        logger.info('[WEB0S PLAYER] Same audioTrack, nothing to do');
        resolve(true);
      } else {
        // Little hack to avoid LG WebOS freeze 1/2
        let currentTime = Math.floor(this.getCurrentTime());

        logger.debug(
          '[WEB0S PLAYER] setAudioTrack saving currentTime> ',
          this.seek_resume,
          currentTime,
          this.currentAudioTrackIndex,
          this.audioTracks
        );
        if (this.seek_resume > 0) {
          if (currentTime === 0) {
            currentTime = this.seek_resume;
          }
        }

        if (this.currentAudioTrackIndex !== null) {
          this.audioTracks[this.currentAudioTrackIndex].enabled = false;
        }

        // Little hack for LG 2017 and 2016 (and maybe 2015) :S
        // video tag html catch audioTracks like above, there is no unique id,
        // there is no kind and iso languange id, there is no label :S :s
        // when user switch lang, disable html5 tracks at the same time, before
        // send request to LG luna js
        // IMPORTANT: It does not apply for LG webos <= 2014
        this.disableHTML5AudioTracks();
        logger.debug('[WEB0S PLAYER] setAudioTrack after disable audioTracks');

        logger.info(
          `[WEB0S PLAYER] Set audioTrack to index: ${internalAudioIndex}, lang code: ${codeTrack}`
        );
        const pid = this.getWebosMediaId();
        logger.debug('[WEB0S] Mandando a luna js index> ', internalAudioIndex, pid);
        logger.debug('[WEB0S] current time before change audio> ', currentTime);
        this.webOS.service.request('luna://com.webos.media', {
          method: 'selectTrack',
          parameters: {
            type: 'audio',
            index: internalAudioIndex, //  index of desired audio track
            mediaId: pid
          },
          onSuccess: () => {
            logger.debug(
              '[WEB0S PLAYER] success when selectAudioTrack, calling callbackonSuccessChangeAudio, params codeTrack & seekTime> ',
              codeTrack,
              currentTime
            );
            this.onSuccessChangeAudio(internalAudioIndex, currentTime);
            resolve(true);
          },
          onFailure: err => {
            logger.error(
              '[WEB0S PLAYER] An error occured when changing audio track... :('
            );
            // logger.log("[WEB0S PLAYER] error: " + JSON.stringify(err));
            reject('An error occured when changing audio track...');
          }
        });
      }
    });
  }

  getAudioIndexOfCode(codeTrack) {
    let ret = null;

    for (let j = 0; j < this.audioTracks.length; j++) {
      if (this.audioTracks[j].id === codeTrack) {
        ret = j;
        break;
      }
    }

    return ret;
  }

  onSuccessChangeAudio(codeTrack, seekTime) {
    logger.debug(
      '[WEB0S PLAYER] enter onSuccessChangeAudio, codeTrack & seekTime> ',
      codeTrack,
      seekTime
    );
    // Esta validacion evita reinincio del contenido reproducido
    if(this.sdkVersion >= 3) {
      seekTime = this._playerHTMLTag.currentTime;
    }
    // Esperar dos segundos al player para forzar el rebuffering al cambiar idioma
    setTimeout(() => {
      if (this.getCurrentPlayerState() === PONPLAY) {
        this.seek(seekTime);
      }
      this.currentAudioTrackIndex = codeTrack;
    }, 500);
  }

  getWebosMediaId() {
    if (this._playerHTMLTag) {
      return this._playerHTMLTag.mediaId;
    }

    return '';
  }

  setAudioTrackLive(codeTrack, subtitleTrack) {
    logger.debug('setAudioTrackLive', codeTrack)
    this.setAudioTrack(codeTrack)
  }

  /* END MULTIPLE AUDIOTRACKS */
}

export default Web0sPlayer;



// WEBPACK FOOTER //
// ./src/devices/web0s/Player.js